import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Copy, ExternalLink, Globe, Power, PowerOff, Save } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Phase F.5 Batch 15 — Public Catalog Export admin page. */
export default function PublicCatalog() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [whitelistText, setWhitelistText] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/catalog/config`);
      setCfg(r.data);
      setWhitelistText((r.data?.category_whitelist || []).join(", "));
    } catch (e) { toast.error("Failed to load catalog config"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const update = (patch) => setCfg((p) => ({ ...p, ...patch }));

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        slug: cfg.slug, title: cfg.title, description: cfg.description,
        brand_color: cfg.brand_color, logo_url: cfg.logo_url,
        show_prices: cfg.show_prices, show_services: cfg.show_services, show_goods: cfg.show_goods,
        contact_email: cfg.contact_email, contact_phone: cfg.contact_phone,
        category_whitelist: whitelistText.split(",").map((s) => s.trim()).filter(Boolean),
      };
      const r = await axios.put(`${API}/catalog/config`, body);
      setCfg(r.data);
      toast.success("Saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const publish = async () => {
    try { const r = await axios.post(`${API}/catalog/config/publish`); setCfg(r.data); toast.success("Catalog is live"); }
    catch (e) { toast.error("Publish failed"); }
  };
  const unpublish = async () => {
    try { const r = await axios.post(`${API}/catalog/config/unpublish`); setCfg(r.data); toast.success("Catalog hidden"); }
    catch (e) { toast.error("Unpublish failed"); }
  };

  if (!cfg) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;

  const baseUrl = `${process.env.REACT_APP_BACKEND_URL}/api/catalog/public/${cfg.slug}`;
  const links = [
    { label: "JSON", url: baseUrl },
    { label: "CSV", url: `${baseUrl}/export.csv` },
    { label: "PDF", url: `${baseUrl}/export.pdf` },
    { label: "Embed HTML", url: `${baseUrl}/embed` },
  ];
  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copied"); };

  return (
    <div className="space-y-4 p-4" data-testid="public-catalog-page">
      <div>
        <h1 className="text-2xl font-bold">Public Catalog</h1>
        <p className="text-sm text-muted-foreground">
          Expose your services + goods as a public, brandable page. Share the link, embed as an iframe, or export as PDF/CSV.
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5" /> Status
                {cfg.is_published
                  ? <Badge className="ml-2 bg-emerald-600">Published</Badge>
                  : <Badge variant="secondary" className="ml-2">Unpublished</Badge>}
              </CardTitle>
              <CardDescription>Toggle whether the public URL responds with the catalog or 404s.</CardDescription>
            </div>
            <div className="flex gap-1">
              {!cfg.is_published
                ? <Button onClick={publish} className="bg-emerald-600 hover:bg-emerald-700" data-testid="pc-publish-btn"><Power className="h-4 w-4 mr-1" /> Publish</Button>
                : <Button onClick={unpublish} variant="outline" data-testid="pc-unpublish-btn"><PowerOff className="h-4 w-4 mr-1" /> Unpublish</Button>}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {links.map((l) => (
            <div key={l.label} className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline">{l.label}</Badge>
              <code className="text-xs flex-1 truncate bg-muted px-2 py-1 rounded" data-testid={`pc-link-${l.label.toLowerCase().replace(" ", "-")}`}>{l.url}</code>
              <Button size="sm" variant="outline" onClick={() => copy(l.url)}><Copy className="h-3.5 w-3.5" /></Button>
              <Button size="sm" variant="outline" onClick={() => window.open(l.url, "_blank")}><ExternalLink className="h-3.5 w-3.5" /></Button>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Branding & Visibility</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><Label>Slug</Label><Input value={cfg.slug} onChange={(e) => update({ slug: e.target.value })} data-testid="pc-slug" /></div>
            <div><Label>Title</Label><Input value={cfg.title} onChange={(e) => update({ title: e.target.value })} data-testid="pc-title" /></div>
            <div className="md:col-span-2"><Label>Description</Label>
              <Textarea rows={2} value={cfg.description || ""} onChange={(e) => update({ description: e.target.value })} data-testid="pc-description" />
            </div>
            <div><Label>Brand color (hex)</Label><Input value={cfg.brand_color || ""} onChange={(e) => update({ brand_color: e.target.value })} data-testid="pc-brand-color" /></div>
            <div><Label>Logo URL</Label><Input value={cfg.logo_url || ""} onChange={(e) => update({ logo_url: e.target.value })} data-testid="pc-logo-url" /></div>
            <div><Label>Contact email</Label><Input value={cfg.contact_email || ""} onChange={(e) => update({ contact_email: e.target.value })} data-testid="pc-contact-email" /></div>
            <div><Label>Contact phone</Label><Input value={cfg.contact_phone || ""} onChange={(e) => update({ contact_phone: e.target.value })} data-testid="pc-contact-phone" /></div>
            <div className="md:col-span-2"><Label>Category whitelist (comma-separated; empty = all)</Label>
              <Input value={whitelistText} onChange={(e) => setWhitelistText(e.target.value)} placeholder="e.g. lawn, plumbing, electrical" data-testid="pc-whitelist" />
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-4">
            <label className="text-sm flex items-center gap-2">
              <Switch checked={!!cfg.show_prices} onCheckedChange={(v) => update({ show_prices: v })} data-testid="pc-show-prices" /> Show prices
            </label>
            <label className="text-sm flex items-center gap-2">
              <Switch checked={!!cfg.show_services} onCheckedChange={(v) => update({ show_services: v })} data-testid="pc-show-services" /> Show services
            </label>
            <label className="text-sm flex items-center gap-2">
              <Switch checked={!!cfg.show_goods} onCheckedChange={(v) => update({ show_goods: v })} data-testid="pc-show-goods" /> Show goods
            </label>
          </div>
          <div className="mt-4">
            <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="pc-save-btn">
              <Save className="h-4 w-4 mr-1" /> {saving ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
